'use client';

import React, { useState } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { APP_NAME, APP_LOGO_PATH, APP_TAGLINE, APP_DESCRIPTION } from '@/lib/appConfig';

export default function LoginPage() {
  const router = useRouter();
  const supabase = createClient();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const { data, error: authError } = await supabase.auth.signInWithPassword({ email, password });
      if (authError) throw authError;
      if (data.user) {
        const { data: profile } = await supabase.from('profiles').select('role').eq('id', data.user.id).single();
        const role = profile?.role;
        if (role === 'SUPER_ADMIN') router.push('/super-admin-global-dashboard');else
        if (role === 'COMPANY_ADMIN') router.push('/company-admin-operations-center');else
        if (role === 'PROJECT_MANAGER') router.push('/project-manager-command-dashboard');else
        if (role === 'RRHH') router.push('/personnel-management-hub');else
        router.push('/developer-project-portal');
      }
    } catch (err: any) {
      setError(err.message === 'Invalid login credentials' ? 'Credenciales inválidas. Verifica tu email y contraseña.' : err.message || 'Error al iniciar sesión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Panel - Navy Branding */}
      <div className="hidden lg:flex lg:w-1/2 bg-slate-900 flex-col items-center justify-center p-12 relative overflow-hidden">
        {/* Background decorations */}
        <div className="absolute top-0 left-0 w-full h-full">
          <div className="absolute top-20 left-20 w-64 h-64 bg-sky-500/10 rounded-full blur-3xl" />
          <div className="absolute bottom-20 right-20 w-80 h-80 bg-sky-500/5 rounded-full blur-3xl" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-slate-800/50 rounded-full blur-3xl" />
        </div>
        <div className="relative z-10 text-center">
          <div className="flex items-center justify-center mb-8">
            <div className="w-20 h-20 bg-sky-500/20 rounded-2xl flex items-center justify-center border border-sky-500/30">
              <img src={APP_LOGO_PATH} alt={APP_NAME} className="h-12 w-auto object-contain" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-white mb-3">{APP_NAME}</h1>
          <p className="text-sky-400 text-lg font-medium mb-2">{APP_TAGLINE}</p>
          <p className="text-slate-400 text-sm mb-12">{APP_DESCRIPTION}</p>
          <div className="grid grid-cols-2 gap-4 text-left">
            {[
            { icon: '🏢', title: 'Multi-empresa', desc: 'Gestiona múltiples compañías' },
            { icon: '👥', title: 'Personal', desc: 'Control de desarrolladores' },
            { icon: '📊', title: 'Analítica', desc: 'Reportes en tiempo real' },
            { icon: '🔗', title: 'Git Integration', desc: 'Conecta repositorios' }].
            map((item) =>
            <div key={item.title} className="bg-slate-800/50 rounded-xl p-4 border border-slate-700/50">
                <div className="text-2xl mb-2">{item.icon}</div>
                <div className="text-white text-sm font-semibold">{item.title}</div>
                <div className="text-slate-400 text-xs">{item.desc}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8 bg-slate-900 overflow-y-auto">
        <div className="w-full max-w-md">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center justify-center gap-3 mb-8">
            <img src={APP_LOGO_PATH} alt={APP_NAME} className="h-10 w-auto" />
            <span className="text-2xl font-bold text-white">{APP_NAME}</span>
          </div>

          <div className="bg-slate-800 rounded-2xl shadow-xl border border-slate-700 p-8">
            <div className="mb-8">
              <h2 className="text-2xl font-bold text-white">Bienvenido de vuelta</h2>
              <p className="text-slate-400 text-sm mt-1">Ingresa tus credenciales para continuar</p>
            </div>

            {error &&
            <div className="mb-5 flex items-start gap-3 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
                <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                <span>{error}</span>
              </div>
            }

            <form onSubmit={handleLogin} className="space-y-5">
              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Correo electrónico</label>
                <div className="relative">
                  <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" /></svg>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="tu@empresa.com"
                    className="w-full pl-10 pr-4 py-3 border border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-slate-900 text-white transition-all" />

                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-slate-300 mb-2">Contraseña</label>
                <div className="relative">
                  <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" /></svg>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    placeholder="••••••••"
                    className="w-full pl-10 pr-12 py-3 border border-slate-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-sky-500 focus:border-transparent bg-slate-900 text-white transition-all" />

                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors">
                    {showPassword ?
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" /></svg> :

                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                    }
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-end">
                <a href="/forgot-password" className="text-sm text-sky-600 hover:text-sky-700 font-medium transition-colors">¿Olvidaste tu contraseña?</a>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3.5 bg-sky-500 text-white font-semibold rounded-xl hover:bg-sky-600 transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-sky-500/25">

                {loading ?
                <><svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" /></svg>Iniciando sesión...</> :
                'Iniciar Sesión'}
              </button>
            </form>
          </div>

          <p className="text-center text-slate-400 text-xs mt-6">© {new Date().getFullYear()} {APP_NAME}</p>
        </div>
      </div>
    </div>);

}